import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ScanLine, FileText, CalendarCheck, MessageCircle, ShieldCheck, Check, AlertTriangle, ArrowLeft } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { api, track } from "@/lib/api";
import { waLink } from "@/constants/site";

export default function SurfacePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    track("page_view", { page: "surface", slug });
    api.get(`/surface-guide/${slug}`).then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-gray-500" data-testid="surface-loading">Loading…</div>;
  if (!data) return <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4"><p className="text-gray-400">Surface not found.</p><button onClick={() => navigate("/protection")} className="text-[#C9A45C]">Back to Protection</button></div>;

  const wa = data.assessment_required
    ? `Hi ERRAH, I'd like a professional assessment for my ${data.title.toLowerCase()} surface. Could you help me find the right protection?`
    : `Hi ERRAH, I am interested in protection for my ${data.title.toLowerCase()} surface. I'd like to know which ERRAH service is suitable and get a quotation.`;

  const quoteState = { surface_type: data.title, material: data.materials?.[0], source: "Surface Page",
    preferred_service: data.services?.[0]?.service_name || "", recommended_service: data.services?.[0]?.service_name || "",
    scan: { surface: data.title, recommended_service: data.services?.[0]?.service_name } };
  const bookState = { service: data.assessment_required ? "Site inspection" : (data.services?.[0]?.service_name || ""),
    recommended_service: data.services?.[0]?.service_name, surface_type: data.title, material: data.materials?.[0] };

  return (
    <div className="bg-[#080808]" data-testid="surface-page">
      <Seo title={`${data.title} Protection`} description={data.description} />

      {/* HERO */}
      <section className="relative min-h-[52vh] flex items-end overflow-hidden">
        <img src={data.image} alt={data.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
        <div className="relative max-w-[1500px] mx-auto px-5 lg:px-10 w-full pb-12 pt-28">
          <button onClick={() => navigate("/protection")} className="inline-flex items-center gap-1.5 text-xs tracking-widest text-gray-300 hover:text-[#C9A45C] mb-5 transition-colors" data-testid="surface-back">
            <ArrowLeft size={14} /> ALL SURFACES
          </button>
          <p className="errah-label gold-text mb-3">Surface Protection</p>
          <h1 className="serif-head text-4xl sm:text-5xl lg:text-7xl" data-testid="surface-title">{data.title}</h1>
          <p className="mt-4 text-lg text-gray-200 max-w-2xl font-light">{data.tagline}</p>
        </div>
      </section>

      <div className="max-w-[1500px] mx-auto px-5 lg:px-10 py-14 grid lg:grid-cols-[1fr_320px] gap-10 items-start">
        <div>
          <p className="text-gray-400 leading-relaxed max-w-2xl">{data.description}</p>

          {/* Applications */}
          <div className="mt-9">
            <p className="errah-label gold-text mb-3">Suitable applications</p>
            <div className="flex flex-wrap gap-2" data-testid="surface-applications">
              {data.applications?.map((a) => (
                <span key={a} className="text-sm px-4 py-2 hair-border bg-white/[0.02] text-gray-200">{a}</span>
              ))}
            </div>
          </div>

          {/* Protection options */}
          <div className="mt-11">
            <p className="errah-label gold-text mb-4">Available ERRAH protection</p>

            {data.assessment_required ? (
              <div className="hair-border bg-white/[0.02] p-7" data-testid="surface-assessment">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="text-[#E4C987]" size={20} />
                  <h3 className="font-display text-xl">Professional assessment recommended</h3>
                </div>
                <p className="text-sm text-gray-400 leading-relaxed max-w-xl">
                  This surface needs a tailored approach. Our team will assess the material and finish on site and recommend the right ERRAH solution.
                </p>
              </div>
            ) : (
              <div className="space-y-3.5" data-testid="surface-services">
                {data.services.map((s) => (
                  <div key={s.id} className="hair-border bg-white/[0.02] p-6 grid md:grid-cols-[1fr_auto] gap-5 items-start" data-testid={`surface-service-${s.id}`}>
                    <div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-display text-xl">{s.service_name}</h3>
                        <span className="text-[10px] tracking-widest px-2 py-1 border border-[#C9A45C]/40 text-[#E4C987] uppercase">{s.protection_type}</span>
                      </div>
                      <p className="mt-2.5 text-sm text-gray-400 leading-relaxed max-w-xl">{s.description}</p>
                      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-gray-400">
                        {s.warranty && <span className="inline-flex items-center gap-1.5"><ShieldCheck size={13} className="text-[#C9A45C]" /> {s.warranty} warranty</span>}
                        {s.applies_to?.length > 0 && <span className="inline-flex items-center gap-1.5"><Check size={13} className="text-[#C9A45C]" /> {s.applies_to.join(", ")}</span>}
                        {s.site_inspection_required && <span className="text-[#E4C987]">Site inspection recommended</span>}
                      </div>
                    </div>
                    <div className="md:text-right">
                      <p className="errah-label mb-1">From</p>
                      <p className="font-display text-lg gold-text whitespace-nowrap">{s.price_label}</p>
                    </div>
                  </div>
                ))}
                <p className="text-[11px] text-gray-600 pt-1">Prices are indicative starting points. Final pricing depends on surface condition, dimensions, preparation and installation.</p>
              </div>
            )}
          </div>
        </div>

        {/* Sticky action rail */}
        <aside className="lg:sticky lg:top-24 space-y-3" data-testid="surface-actions">
          <div className="hair-border bg-gradient-to-b from-[#C9A45C]/[0.08] to-transparent p-6">
            <div className="flex items-center gap-2 mb-2"><ScanLine size={16} className="text-[#C9A45C]" /><p className="errah-label gold-text">Not sure what you need?</p></div>
            <p className="text-sm text-gray-300 leading-relaxed">Let ERRAH AI identify your surface and recommend the right protection with an instant estimate.</p>
            <button onClick={() => navigate("/ai-scan")} data-testid="surface-scan-cta"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#C9A45C] text-black py-3.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors">
              <ScanLine size={16} /> Scan Your Surface
            </button>
          </div>
          <button onClick={() => { track("quote_started", { source: "surface" }); navigate("/quote", { state: quoteState }); }} data-testid="surface-quote-cta"
            className="w-full inline-flex items-center justify-center gap-2 border border-white/20 py-3.5 text-sm hover:bg-white hover:text-black transition-colors">
            <FileText size={16} /> {data.assessment_required ? "Request a Quote" : "Get a Quote"}
          </button>
          <button onClick={() => { track("booking_started", { source: "surface" }); navigate("/booking", { state: bookState }); }} data-testid="surface-book-cta"
            className="w-full inline-flex items-center justify-center gap-2 border border-white/20 py-3.5 text-sm hover:bg-white hover:text-black transition-colors">
            <CalendarCheck size={16} /> {data.assessment_required ? "Book Site Inspection" : "Book Installation"}
          </button>
          <a href={waLink(wa)} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { location: "surface", slug })} data-testid="surface-whatsapp-cta"
            className="w-full inline-flex items-center justify-center gap-2 border border-white/20 py-3.5 text-sm hover:bg-white hover:text-black transition-colors">
            <MessageCircle size={16} /> WhatsApp ERRAH
          </a>
        </aside>
      </div>
    </div>
  );
}
